import * as React from 'react';
import {EquipmentItem} from '../../types';
import {graphql, useLazyLoadQuery} from "react-relay";
import {Link, useNavigate} from "react-router-dom";
import {addToCart} from "../cart";
import {EquipmentQuery} from "./__generated__/EquipmentQuery.graphql";


const QUERY = graphql`
    query EquipmentQuery {
        rentalItems(first: 8) {
            nodes {
                id
                assetTag
                name
                description
                image
                category
                specifications
                dailyRate
                kitMembers {
                    id
                }
            }
        }
    }`


const Equipment: React.FC = () => {
    const equipment = useLazyLoadQuery<EquipmentQuery>(QUERY, {})
    const navigate = useNavigate();
    return (
        <section id="equipment" className="py-24 bg-black relative border-t border-gray-900">
            <div className="max-w-7xl mx-auto px-6">
                <div className="flex flex-col md:flex-row justify-between items-end mb-12">
                    <div>
                        <span
                            className="text-lumina-purple font-display text-sm tracking-wider uppercase">Gear Locker</span>
                        <h2 className="text-4xl md:text-5xl font-display font-bold text-white mt-2">
                            EQUIPMENT <span className="text-gray-600">RENTAL</span>
                        </h2>
                    </div>
                    <button
                        onClick={() => navigate('/book')}
                        className="hidden md:block px-6 py-3 border border-gray-700 text-gray-300 hover:border-lumina-accent hover:text-white transition-all duration-300 text-sm font-bold tracking-widest">
                        BOOK GEAR
                    </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {equipment.rentalItems.nodes.map((item) => (
                        <div key={item.id}
                             className="group bg-[#050505] border border-gray-800 p-4 hover:border-lumina-purple/50 transition-all duration-300 flex flex-col h-full">
                            <div className="relative aspect-square overflow-hidden mb-4 bg-gray-900">
                                {item.image ? (
                                    <img
                                        src={item.image}
                                        alt={item.name}
                                        className="w-full h-full object-contain bg-white p-3 group-hover:scale-105 transition-transform duration-500 opacity-90 group-hover:opacity-100"
                                    />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-gray-700 font-display text-xs tracking-widest">
                                        {item.assetTag}
                                    </div>
                                )}
                                <div
                                    className="absolute top-2 left-2 bg-lumina-purple/90 text-white text-xs font-bold px-2 py-1 uppercase">
                                    {item.category.replace(/_/g, ' ')}
                                </div>
                                {item.kitMembers.length > 0 && (
                                    <div
                                        className="absolute bottom-2 right-2 bg-black/80 text-gray-300 text-[10px] font-bold px-2 py-1 uppercase">
                                        {item.kitMembers.length} pieces
                                    </div>
                                )}
                            </div>

                            <h3 className="text-lg font-display font-bold text-white mb-1 group-hover:text-lumina-purple transition-colors">
                                <Link to={`/gear/${item.assetTag}`}>{item.name}</Link>
                            </h3>
                            <p className="text-gray-500 text-xs mb-3 truncate">{item.description ?? item.assetTag}</p>

                            <div className="flex flex-wrap gap-2 mb-4">
                                {item.specifications.map((spec, i) => (
                                    <span key={i}
                                          className="text-[10px] text-gray-400 bg-gray-900 px-2 py-1 border border-gray-800">
                    {spec}
                  </span>
                                ))}
                            </div>

                            <div className="flex items-center justify-between mt-auto border-t border-gray-800 pt-4">
                                {/* Fixed height so tiles line up whether there's a rate or not. */}
                                <div className="flex flex-col justify-end h-12">
                                    <span
                                        className="text-[10px] text-gray-500 uppercase tracking-wider leading-none mb-1">
                                        {item.dailyRate ? 'Starting at' : 'Pricing'}
                                    </span>
                                    <span className="font-display font-bold leading-none h-6 flex items-end">
                                        {item.dailyRate ? (
                                            <span className="text-lumina-accent">${item.dailyRate}<span
                                                className="text-gray-600 text-sm font-sans font-normal">/day</span></span>
                                        ) : (
                                            <span className="text-gray-400 text-sm">On request</span>
                                        )}
                                    </span>
                                </div>
                                <button
                                    onClick={() => { addToCart({assetTag: item.assetTag, name: item.name, dailyRate: item.dailyRate, pieces: item.kitMembers.length}); navigate('/book'); }}
                                    title="Add to your rental request"
                                    className="w-8 h-8 flex items-center justify-center bg-white text-black hover:bg-lumina-accent transition-colors">
                                    +
                                </button>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="mt-8 md:hidden text-center">
                    <button
                        onClick={() => navigate('/book')}
                        className="px-6 py-3 border border-gray-700 text-gray-300 w-full hover:border-lumina-accent hover:text-white transition-all duration-300 text-sm font-bold tracking-widest">
                        BOOK GEAR
                    </button>
                </div>
            </div>
        </section>
    );
};

export default Equipment;
